import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { PublicLayout, Section, Eyebrow } from "@/components/PublicLayout";
import { centreCopy, checklistLabel, TONES } from "@/lib/centre";
import { ArrowRight, Plus, GraduationCap, Check, Circle } from "lucide-react";

const STAGE_TONE = (s) => {
  if (["Admitted", "Enrolled", "Offer accepted", "Offer sent", "Conditionally admitted"].includes(s)) return "bg-green-100 text-green-800";
  if (["Not admitted", "Waitlisted"].includes(s)) return "bg-red-100 text-red-700";
  return "bg-heraldic/10 text-heraldic";
};
const PRIORITY = { decision: 100, ready_to_submit: 90, interview: 80, assessment_in_progress: 70,
  assessment_required: 65, assessment_payment: 60, in_progress: 50, under_review: 20, enrolled: 10 };

export default function ApplicationCentre() {
  const { user, logout } = useAuth();
  const { lang } = useI18n();
  const [apps, setApps] = useState(null);
  useEffect(() => { api.get("/me/applications").then((r) => setApps(r.data)).catch(() => setApps([])); }, []);

  const L = lang === "fr"
    ? { eyebrow: "Espace candidature", welcome: "Bienvenue", intro: "Le foyer d'admission de votre famille. Gérez chaque candidat, reprenez à tout moment et suivez chaque étape, de la candidature à la décision.", logout: "Se déconnecter", top: "Action prioritaire", open: "Ouvrir", add: "Démarrer une nouvelle candidature", addNote: "Ajouter un autre enfant ou un nouveau programme", loading: "Chargement…", childA: "Candidat enfant", edu: "Éducateur", he: "Candidat enseignement supérieur" }
    : { eyebrow: "Application Centre", welcome: "Welcome", intro: "Your family's admissions home. Manage each applicant, resume any time, and track every stage from application to decision.", logout: "Log out", top: "Your priority action", open: "Open", add: "Start a new application", addNote: "Add another child or a new programme", loading: "Loading…", childA: "Child applicant", edu: "Educator", he: "Higher-education applicant" };

  const sorted = apps ? [...apps].sort((a, b) => (PRIORITY[b.status_state] || 0) - (PRIORITY[a.status_state] || 0)) : [];
  const hero = sorted.find((a) => (PRIORITY[a.status_state] || 0) >= 50);
  const heroCopy = hero ? centreCopy(hero.status_state, lang) : null;

  return (
    <PublicLayout>
      <Section className="py-16 md:py-24">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Eyebrow>{L.eyebrow}</Eyebrow>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink" data-testid="centre-title">{L.welcome}, {user?.name?.split(" ")[0] || ""}</h1>
            <p className="mt-3 text-ink/60 max-w-xl">{L.intro}</p>
          </div>
          <button data-testid="centre-logout" onClick={logout} className="text-sm text-ink/50 hover:text-ink">{L.logout}</button>
        </div>

        {hero && heroCopy && (
          <Link to={`/centre/${hero.application_id}`} data-testid="centre-hero"
            className={`mt-8 block border-l-4 rounded-r-sm p-6 md:p-7 transition-colors ${TONES[heroCopy.tone]}`}>
            <div className="eyebrow text-heraldic mb-1.5">{L.top} · {hero.application_id}</div>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-2xl">
                <h2 className="font-serif text-2xl md:text-3xl text-ink">{heroCopy.title}</h2>
                <p className="mt-1.5 text-ink/70">{heroCopy.sub}</p>
                <p className="mt-1 text-sm text-ink/45">{hero.applicant_name}</p>
              </div>
              {heroCopy.cta && (
                <span className="inline-flex items-center gap-2 bg-heraldic text-white px-6 py-3 rounded-sm text-sm whitespace-nowrap" data-testid="centre-hero-cta">
                  {heroCopy.cta} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </div>
          </Link>
        )}

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {apps && apps.map((a) => {
            const copy = centreCopy(a.status_state, lang);
            return (
              <Link key={a.application_id} to={`/centre/${a.application_id}`} data-testid={`app-card-${a.application_id}`}
                className="group border border-border rounded-sm p-6 hover:border-heraldic transition-colors bg-paper">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-ink/45">{a.application_id}</div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${STAGE_TONE(a.stage)}`}>{a.stage}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-heraldic" />
                  <div className="font-serif text-xl text-ink">{a.applicant_name}</div>
                </div>
                <div className="text-sm text-ink/55 mt-1">{a.applicant_type === "child" ? L.childA : a.applicant_type === "educator" ? L.edu : L.he} · {a.market}</div>
                <div className="mt-4 border-t border-border pt-3">
                  <div className="text-sm font-medium text-ink">{copy.title}</div>
                  <div className="text-sm text-ink/60 mt-0.5">{copy.sub}</div>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  {(a.checklist || []).map((c) => (
                    <span key={c.key} className={`inline-flex items-center gap-1 text-xs ${c.done ? "text-green-700" : "text-ink/40"}`}>
                      {c.done ? <Check className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {checklistLabel(c.key, lang)}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-1 text-heraldic text-sm">{L.open} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </Link>
            );
          })}

          <Link to="/apply" data-testid="centre-add" className="border border-dashed border-border rounded-sm p-6 flex flex-col items-center justify-center text-center text-ink/50 hover:border-heraldic hover:text-heraldic transition-colors min-h-[180px]">
            <Plus className="h-7 w-7" />
            <div className="mt-2 font-serif text-lg">{L.add}</div>
            <div className="text-xs mt-1">{L.addNote}</div>
          </Link>
        </div>
        {apps === null && <p className="mt-8 text-ink/50">{L.loading}</p>}
      </Section>
    </PublicLayout>
  );
}
